import { action } from '@/lib/api';
import doDebounce from '@/lib/debounce';
import sendToast from '@/lib/sendToast';
import sendToastSyncError from '@/lib/sendToastSyncError';
import { getAppIssues, getApps, getCredentialById, getIntegrationsProject, getMockTypes, getPlatformSecrets, getWorkflows, getWorkflowSteps, putSyncBatch } from '@/redux/actions/gravityActions';
import { getUserCompanies } from '@/redux/actions/userActions';
import selectors from '@/redux/selectors/selectors';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

const syncIntervalMs = 1000;
const syncUpdateIntervalMs = 300;
const syncUpdateDebounceMs = 1500;
const forceSyncDelayMs = 2000;

const SyncHandler = () => {
  const changesQueue = useSelector((state) => state.workflow.changesQueue);
  const updateQueue = useSelector((state) => state.workflow.updateQueue);
  const isSyncing = useSelector((state) => state.workflow.isSyncing);
  const isUpdating = useSelector((state) => state.workflow.isUpdating);
  const syncFailed = useSelector((state) => state.workflow.syncFailed);
  const companyId = useSelector(selectors.companyId);
  const userId = useSelector(selectors.userId);
  const admin = useSelector(selectors.admin);
  const companyIdRef = useRef(companyId);
  const changesQueueRef = useRef([]);
  const changesQueueLastChangeRef = useRef(new Date());
  const updateQueueRef = useRef([]);
  const isSyncingRef = useRef(false);
  const isUpdatingRef = useRef(false);
  const lastSyncTimestampRef = useRef(null);
  const isDebouncingRef = useRef(false);

  /**
   * Local changes are filed into the changesQueue. Here, we wait until no updates
   * are being made (after debounce) and then send those batch changes to the server.
   */
  const syncInterval = async () => {
    if (isSyncingRef.current === true || isUpdatingRef.current === true || changesQueueRef.current.length === 0) {
      return;
    }

    action('SET_IS_SYNCING', true);
    const batch = [...changesQueueRef.current];
    action('FLUSH_CHANGES_QUEUE');
    try {
      const res = await putSyncBatch({ batch });
      const isSuccess = res?.success === true;
      const isVersionMismatch = res?.failed?.some((el) => el?.error === 'VERSION_MISMATCH');

      if (admin && !isSuccess) {
        const message = isVersionMismatch ? 'Sync handler failed due to version mismatch' : 'Sync handler failed due to unknown error, check network tab';
        sendToast({ message, type: 'error' });
      }
    } catch (error) {
      action('SET_SYNC_FAILED', true);
    }
    action('SET_IS_SYNCING', false);
    lastSyncTimestampRef.current = null;
    isDebouncingRef.current = false;
  };

  const debouncedSyncInterval = useRef(doDebounce(syncInterval, syncIntervalMs));

  /**
   * If there are changes in queue, no debounce happening,
   * and it's been longer than the interval, then we should force a sync.
   */
  const forceSyncCheck = () => {
    const timeSinceLastSync = new Date() - (lastSyncTimestampRef.current || new Date());
    const longEnoughSinceLastSyncOrNoLastSync = timeSinceLastSync >= forceSyncDelayMs || !lastSyncTimestampRef.current;

    if (!isDebouncingRef.current && changesQueueRef.current.length > 0 && longEnoughSinceLastSyncOrNoLastSync) {
      syncInterval();
    }
  };

  /**
   * Updates that come in from other users or as a response to the putSyncBatch function
   * get filed into the updateQueue. Here, we wait until no local changes are being made
   * and then request those resources newly.
   */
  const syncUpdateInterval = async () => {
    if (isSyncingRef.current === true || isUpdatingRef.current === true || updateQueueRef.current.length === 0 || changesQueueRef.current.length > 0) {
      return;
    }

    const timeSinceLastChange = new Date() - changesQueueLastChangeRef.current;
    if (timeSinceLastChange < syncUpdateDebounceMs) {
      return;
    }

    action('SET_IS_UPDATING', true);
    const batch = [...updateQueueRef.current]?.filter((el) => (el.type !== 'app' && el.type !== 'mockType' ? el.companyId === companyIdRef.current : true));
    action('FLUSH_UPDATE_QUEUE');

    try {
      const appBatch = batch.filter((el) => el.type === 'app').map((el) => ({ appId: el.appId, appVersion: el.appVersion }));
      if (appBatch.length > 0) {
        await getApps({ versions: JSON.stringify(appBatch), useCache: false });
      }

      const workflowStepBatch = batch.filter((el) => el.type === 'workflowStep');
      const workflowStepBatchIds = workflowStepBatch.map((el) => el.stepId);
      if (workflowStepBatchIds.length > 0) {
        await getWorkflowSteps({ ids: JSON.stringify(workflowStepBatchIds) });
      }

      const workflowBatch = batch.filter((el) => el.type === 'workflow').map((el) => el.workflowId);
      if (workflowBatch.length > 0) {
        await getWorkflows({ ids: JSON.stringify(workflowBatch) });
      }

      const companyBatch = batch.filter((el) => el.type === 'company').map((el) => el.workflowId);
      if (companyBatch.length > 0) {
        await getUserCompanies({ userId });
      }

      const credentialBatch = batch.filter((el) => el.type === 'credential');
      if (credentialBatch.length > 0) {
        for (const item of credentialBatch) {
          await getCredentialById(item.credentialId, { secrets: true, companyId: item.companyId, useCache: false });
        }
      }

      const platformSecretBatch = batch.filter((el) => el.type === 'platformSecret');
      if (platformSecretBatch.length > 0) {
        const appIds = [...new Set(platformSecretBatch.map((el) => el.appId).filter(Boolean))];
        for (const appId of appIds) {
          await getPlatformSecrets({ appId });
        }
      }

      const issueBatch = batch.filter((el) => el.type === 'issue');
      if (issueBatch.length > 0) {
        await getAppIssues({ appId: issueBatch[0].appId });
      }

      const mockTypeBatch = batch.filter((el) => el.type === 'mockType');
      if (mockTypeBatch.length > 0) {
        await getMockTypes({ ids: JSON.stringify(mockTypeBatch.map((el) => el.mockTypeId)) });
      }

      const projectBatch = batch.filter((el) => el.type === 'project');
      if (projectBatch.length > 0) {
        const projectIds = [...new Set(projectBatch.map((el) => el.projectId))];
        for (const pid of projectIds) {
          await getIntegrationsProject({ projectId: pid });
        }
      }

      // For now, we don't need to sync project issues
      //
      // const projectIssueBatch = batch.filter((el) => el.type === 'projectIssue');
      // if (projectIssueBatch.length > 0) {
      //   const projectIds = [...new Set(projectIssueBatch.map((el) => el.projectId).filter((id) => !!id))];
      //   if (projectIds.length > 0) {
      //     await Promise.all(projectIds.map((pid) => getProjectIssues({ projectId: pid })));
      //   }
      //   await getAllIssues({});
      // }

      action('SET_IS_UPDATING', false);

      const externalUpdates = [];

      if (appBatch?.length > 0) {
        externalUpdates.push(...appBatch?.map((el) => ({ key: `app-${el.appId}`, timestamp: new Date().getTime() })));
      }

      if (workflowStepBatchIds?.length > 0) {
        const externalWorkflowStepChanges = workflowStepBatch?.filter((el) => (el.type === 'workflowStep' ? el.userId !== userId : true));

        if (externalWorkflowStepChanges?.length > 0) {
          externalUpdates.push(...externalWorkflowStepChanges?.map((el) => ({ key: `workflowStep-${el.stepId}`, timestamp: new Date().getTime() })));
        }
      }

      action('SET_EXTERNAL_UPDATES', externalUpdates);
    } catch (error) {
      sendToastSyncError();
    }
  };

  useEffect(() => {
    if (JSON.stringify(changesQueue) !== JSON.stringify(changesQueueRef.current)) {
      changesQueueRef.current = changesQueue;
      changesQueueLastChangeRef.current = new Date();
    }

    if (changesQueue.length > 0) {
      isDebouncingRef.current = true;
      lastSyncTimestampRef.current = new Date();
      debouncedSyncInterval.current();
    }
  }, [JSON.stringify(changesQueue)]);

  useEffect(() => {
    updateQueueRef.current = updateQueue;
  }, [JSON.stringify(updateQueue)]);

  useEffect(() => {
    companyIdRef.current = companyId;
  }, [companyId]);

  useEffect(() => {
    isSyncingRef.current = isSyncing;
  }, [isSyncing]);

  useEffect(() => {
    isUpdatingRef.current = isUpdating;
  }, [isUpdating]);

  useEffect(() => {
    if (syncFailed === true) {
      action('SET_SYNC_FAILED', false);
    }
  }, [syncFailed]);

  useEffect(() => {
    const updateInterval = setInterval(syncUpdateInterval, syncUpdateIntervalMs);
    const forceSyncInterval = setInterval(forceSyncCheck, syncIntervalMs);
    return () => {
      clearInterval(updateInterval);
      clearInterval(forceSyncInterval);
    };
  }, []);

  return null;
};

export default SyncHandler;
